/**
 * Onboarding actions — UI-driven scripted steps.
 * Buy/sell driven transitions live in exchangeActions.ts (next to the trade).
 */
import type { ActionHandler } from '@app-games/core';
import type { GameState } from './state.js';
import {
  onboardingAfterTaps,
  onboardingAfterOpenExchange,
  completedOnboarding,
  ONBOARDING_STARTER_COINS,
} from './onboardingState.js';

export type OnboardingAction =
  | { type: 'ONBOARDING_TAP_CHECK' }
  | { type: 'ONBOARDING_ENTER_EXCHANGE' }
  | { type: 'ONBOARDING_SKIP' };

/** step 0 -> 1 once the tap goal has been reached. */
export const handleOnboardingTapCheck: ActionHandler<
  GameState,
  Extract<OnboardingAction, { type: 'ONBOARDING_TAP_CHECK' }>
> = (state) => {
  const ob = onboardingAfterTaps(state.onboarding, state.statistics.totalClicks);
  if (ob === state.onboarding) return null;
  return { ...state, onboarding: ob };
};

/** step 1 -> 2 on first exchange entry + one-time starter cash top-up. */
export const handleOnboardingEnterExchange: ActionHandler<
  GameState,
  Extract<OnboardingAction, { type: 'ONBOARDING_ENTER_EXCHANGE' }>
> = (state) => {
  const prev = state.onboarding;
  if (prev.done || prev.step !== 1) return null;
  // Top the player up to the starter floor once, so the first trade is meaningful.
  const coins = prev.grantedStarter ? state.coins : Math.max(state.coins, ONBOARDING_STARTER_COINS);
  return { ...state, coins, onboarding: onboardingAfterOpenExchange(prev) };
};

/** Skip the whole flow (from the always-on skip button). */
export const handleOnboardingSkip: ActionHandler<
  GameState,
  Extract<OnboardingAction, { type: 'ONBOARDING_SKIP' }>
> = (state) => {
  if (state.onboarding.done) return null;
  return { ...state, onboarding: completedOnboarding() };
};

export const onboardingActionHandlers = {
  ONBOARDING_TAP_CHECK: handleOnboardingTapCheck,
  ONBOARDING_ENTER_EXCHANGE: handleOnboardingEnterExchange,
  ONBOARDING_SKIP: handleOnboardingSkip,
} as const;
